import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  Clock,
  Eye,
  Sparkles,
  Compass,
  BookOpen,
  Plus,
  Trash2,
  Star,
  MessageSquareText,
  History,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { formatDeDate } from "@/lib/dateFormat";
import { PageHeader, PrimaryButton } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// ---------- Konstanten ----------

const SECTIONS = [
  {
    key: "erfolge",
    number: 1,
    title: "Erfolge und gute Gespräche",
    icon: Sparkles,
    tint: "text-emerald-700",
    dot: "bg-emerald-500",
    placeholder: "Was ist diese Woche gut gelungen? Welches Gespräch trägt weiter?",
  },
  {
    key: "wartende",
    number: 2,
    title: "Offene Zusagen, Fristen und wartende Menschen",
    icon: Clock,
    tint: "text-amber-700",
    dot: "bg-amber-500",
    placeholder: "Was liegt offen, worauf warten wir? Welche Frist läuft?",
  },
  {
    key: "aussichtsreiche",
    number: 5,
    title: "Aussichtsreiche Termine, Menschen und Herausforderungen für die nächste Woche",
    icon: Compass,
    tint: "text-blue-700",
    dot: "bg-blue-500",
    placeholder: "Wo steckt Bewegung, was ist einen zweiten Blick wert?",
  },
  {
    key: "weiterentwicklung",
    number: 6,
    title: "Weiterentwicklung NORA",
    icon: BookOpen,
    tint: "text-violet-700",
    dot: "bg-violet-500",
    placeholder: "Was soll NORA künftig anders angehen? Welche Regel merken wir uns?",
  },
];

const ACTIVITY_CATEGORIES = [
  { value: "info_zugeordnet", label: "Information zugeordnet" },
  { value: "wiedervorlage_gesetzt", label: "Wiedervorlage gesetzt oder angepasst" },
  { value: "zusage_erkannt", label: "Zusage erkannt oder nachverfolgt" },
  { value: "gespraech_vorbereitet", label: "Gespräch oder Termin vorbereitet" },
  { value: "entwurf_vorbereitet", label: "Entwurf vorbereitet" },
  { value: "muster_vorgemerkt", label: "Muster vorgemerkt" },
  { value: "akte_ergaenzt", label: "Kontakt- oder Herausforderungenakte ergänzt" },
  { value: "sonstiges", label: "Sonstiges" },
];
const CATEGORY_LABEL = Object.fromEntries(ACTIVITY_CATEGORIES.map((c) => [c.value, c.label]));

const STATUS_OPTIONS = [
  { value: "automatisch_erledigt", label: "automatisch erledigt", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { value: "zur_pruefung", label: "zur Prüfung", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  { value: "korrigiert", label: "korrigiert", cls: "bg-rose-50 text-rose-700 border-rose-200" },
];
const STATUS_MAP = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.value, s]));

const EVAL_GROUP_A = [
  { value: "richtig_so", label: "Richtig so", requiresNote: false },
  { value: "kuenftig_genauso", label: "Künftig genauso", requiresNote: false, hint: "Speichert eine Lernregel für vergleichbare Fälle." },
  { value: "hier_vorher_fragen", label: "Hier vorher fragen", requiresNote: false },
  { value: "anders_machen", label: "Anders machen", requiresNote: false },
  { value: "nicht_mehr_machen", label: "Nicht mehr machen", requiresNote: false },
];
const EVAL_GROUP_B = [
  { value: "treffend_erkannt", label: "Treffend erkannt" },
  { value: "zu_frueh_vorgelegt", label: "Zu früh vorgelegt" },
  { value: "zu_grob_zusammengefasst", label: "Zu grob zusammengefasst" },
  { value: "wichtiger_hinweis", label: "Wichtiger Hinweis" },
  { value: "nicht_relevant", label: "Nicht relevant" },
];
const EVAL_GROUP_C = [
  { value: "passt_so", label: "Passt so" },
  { value: "persoenlicher_formulieren", label: "Persönlicher formulieren" },
  { value: "kuerzer_und_klarer", label: "Kürzer und klarer" },
  { value: "mehr_kontext_zeigen", label: "Mehr Kontext zeigen" },
  { value: "nur_erinnern", label: "Nur erinnern, nicht vorbereiten" },
];
const EVAL_LABEL = {
  ...Object.fromEntries(EVAL_GROUP_A.map((e) => [e.value, e.label])),
  ...Object.fromEntries(EVAL_GROUP_B.map((e) => [e.value, e.label])),
  ...Object.fromEntries(EVAL_GROUP_C.map((e) => [e.value, e.label])),
  anderes: "Anderes",
};

const AMPEL_DOT = { gruen: "bg-emerald-500", gelb: "bg-amber-400", rot: "bg-rose-500" };

// ---------- Utility ----------

function useWeekParam() {
  const [year, setYear] = useState(null);
  const [week, setWeek] = useState(null);
  return { year, week, setYear, setWeek };
}

function AmpelBadge({ status }) {
  if (!status) return null;
  return <span className={`inline-block w-2 h-2 rounded-full ${AMPEL_DOT[status] || "bg-gray-400"}`} />;
}

function ContactChip({ name, ampel, onClick }) {
  if (!name) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-gray-50 hover:bg-gray-100 border border-gray-200"
    >
      <AmpelBadge status={ampel} />
      {name}
    </button>
  );
}

// ---------- Hauptseite ----------

export default function WeeklyReview() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { year, week, setYear, setWeek } = useWeekParam();

  const params = useMemo(() => {
    if (year && week) return { year, week };
    return {};
  }, [year, week]);

  const { data, isLoading } = useQuery({
    queryKey: ["weekly-review", year, week],
    queryFn: async () =>
      (await api.get(`/weekly-review`, { params })).data,
    keepPreviousData: true,
  });

  // Load contacts + challenges for select-lists (used across dialogs)
  const { data: contactsList } = useQuery({
    queryKey: ["contacts-lite"],
    queryFn: async () => (await api.get(`/contacts`)).data,
  });
  const { data: challengesList } = useQuery({
    queryKey: ["challenges-lite"],
    queryFn: async () => (await api.get(`/challenges`)).data,
  });

  const contactOptions = useMemo(
    () =>
      (contactsList || []).map((c) => ({
        value: c.id,
        label: `${c.first_name} ${c.last_name}`.trim(),
        ampel: c.ampel_status,
      })),
    [contactsList],
  );
  const challengeOptions = useMemo(
    () =>
      (challengesList || []).map((c) => ({ value: c.id, label: c.titel })),
    [challengesList],
  );

  // ---------------- Mutations ----------------

  const invalidate = () => qc.invalidateQueries({ queryKey: ["weekly-review"] });

  const notesMut = useMutation({
    mutationFn: async (freie_notiz) =>
      (await api.put(`/weekly-review/notes`, { freie_notiz }, { params })).data,
    onSuccess: () => {
      invalidate();
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const createEntryMut = useMutation({
    mutationFn: async (payload) =>
      (await api.post(`/weekly-review/entries`, payload, { params })).data,
    onSuccess: () => {
      toast.success("Eintrag gespeichert");
      invalidate();
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const deleteEntryMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/weekly-review/entries/${id}`)).data,
    onSuccess: () => {
      toast.success("Eintrag gelöscht");
      invalidate();
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const createObsMut = useMutation({
    mutationFn: async (payload) =>
      (await api.post(`/nora-observations`, payload)).data,
    onSuccess: () => {
      toast.success("Beobachtung gespeichert");
      invalidate();
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const deleteObsMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/nora-observations/${id}`)).data,
    onSuccess: () => {
      toast.success("Beobachtung gelöscht");
      invalidate();
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const evaluateObsMut = useMutation({
    mutationFn: async ({ id, value, note }) =>
      (await api.post(`/nora-observations/${id}/evaluate`, { value, note })).data,
    onSuccess: () => {
      toast.success("Bewertung gespeichert");
      invalidate();
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const createActivityMut = useMutation({
    mutationFn: async (payload) => (await api.post(`/nora-activities`, payload)).data,
    onSuccess: () => {
      toast.success("Aktivität gespeichert");
      invalidate();
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const deleteActivityMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/nora-activities/${id}`)).data,
    onSuccess: () => {
      toast.success("Aktivität gelöscht");
      invalidate();
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const evaluateActivityMut = useMutation({
    mutationFn: async ({ id, group, value, note }) =>
      (await api.post(`/nora-activities/${id}/evaluate`, { group, value, note })).data,
    onSuccess: (resp) => {
      if (resp?.evaluation_group === "A" && resp?.evaluation_value === "kuenftig_genauso") {
        toast.success("Bewertung + Lernregel gespeichert");
      } else {
        toast.success("Bewertung gespeichert");
      }
      invalidate();
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const closeMut = useMutation({
    mutationFn: async (freie_notiz) =>
      (await api.post(`/weekly-review/close`, { freie_notiz }, { params })).data,
    onSuccess: () => {
      toast.success("Wochenrückblick gespeichert – im Verlauf ergänzt.");
      invalidate();
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  // ---------------- UI State ----------------

  const [entryDialog, setEntryDialog] = useState(null); // { section }
  const [observationDialog, setObservationDialog] = useState(false);
  const [activityDialog, setActivityDialog] = useState(false);
  const [freieNotiz, setFreieNotizState] = useState("");
  const [freieNotizDirty, setFreieNotizDirty] = useState(false);

  // Freie Notiz mit Backend syncen (nur wenn Daten geladen und User nicht gerade tippt)
  const backendNotiz = data?.freie_notiz ?? "";
  if (!freieNotizDirty && backendNotiz !== freieNotiz) {
    setFreieNotizState(backendNotiz);
  }

  if (isLoading || !data) {
    return <div className="p-8 text-sm text-gray-500" data-testid="weekly-review-loading">Lädt Wochenrückblick…</div>;
  }

  const alreadyClosed = (data.logs || []).length > 0;
  const summary = data.summary || { activities_total: 0, activities_by_category: {}, activities_by_status: {}, activities_evaluated: 0 };

  const openContact = (cid) => cid && navigate(`/contacts/${cid}`);

  return (
    <div className="space-y-8" data-testid="weekly-review-page">
      <PageHeader
        title="Wochenrückblick"
        subtitle={
          <span className="flex flex-wrap items-center gap-3">
            <span data-testid="weekly-review-week-label">
              {`KW ${data.iso_week} (${formatDeDate(data.week_start)} – ${formatDeDate(data.week_end)})`}
            </span>
            {alreadyClosed ? (
              <span
                className="inline-block text-xs px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full"
                data-testid="weekly-review-status-badge"
              >
                Zuletzt dokumentiert am {formatDeDate(data.logs[0].closed_at)}
              </span>
            ) : null}
          </span>
        }
        testid="weekly-review-header"
        action={
          <div className="flex items-center gap-2">
            <WeekPicker
              current={{ iso_year: data.iso_year, iso_week: data.iso_week }}
              options={data.available_weeks || []}
              onChange={(y, w) => {
                setYear(y);
                setWeek(w);
                setFreieNotizDirty(false);
              }}
            />
            <PrimaryButton
              onClick={() => closeMut.mutate(freieNotiz || null)}
              disabled={closeMut.isPending}
              data-testid="weekly-review-close-btn"
            >
              <CheckCircle2 className="w-4 h-4 mr-1" />
              {alreadyClosed ? "Wochenrückblick aktualisieren" : "Wochenrückblick abschließen"}
            </PrimaryButton>
          </div>
        }
      />

      <p className="text-sm text-gray-500 max-w-2xl leading-relaxed">
        Diese Ansicht ist wie eine vorbereitete Mitarbeiterbesprechung gedacht:
        NORA zeigt, was diese Woche passiert ist, was intern eigenständig erledigt wurde
        und was für die kommende Woche wichtig sein kann. Alles bleibt jederzeit bearbeitbar.
      </p>

      {/* 1. Erfolge */}
      <TextSection
        section={SECTIONS[0]}
        items={data.entries.erfolge}
        onAdd={() => setEntryDialog({ section: "erfolge" })}
        onDelete={(id) => deleteEntryMut.mutate(id)}
        onOpenContact={openContact}
      />

      {/* 2. Wartende */}
      <TextSection
        section={SECTIONS[1]}
        items={data.entries.wartende}
        onAdd={() => setEntryDialog({ section: "wartende" })}
        onDelete={(id) => deleteEntryMut.mutate(id)}
        onOpenContact={openContact}
      />

      {/* 3. Beobachtungen und Muster */}
      <ObservationsSection
        observations={data.observations}
        onAdd={() => setObservationDialog(true)}
        onDelete={(id) => deleteObsMut.mutate(id)}
        onEvaluate={(id, value, note) => evaluateObsMut.mutate({ id, value, note })}
        onOpenContact={openContact}
      />

      {/* 4. NORA-Aktivitäten */}
      <ActivitiesSection
        activities={data.activities}
        summary={summary}
        onAdd={() => setActivityDialog(true)}
        onDelete={(id) => deleteActivityMut.mutate(id)}
        onEvaluate={(id, group, value, note) => evaluateActivityMut.mutate({ id, group, value, note })}
        onOpenContact={openContact}
      />

      {/* 5. Aussichtsreiche */}
      <TextSection
        section={SECTIONS[2]}
        items={data.entries.aussichtsreiche}
        onAdd={() => setEntryDialog({ section: "aussichtsreiche" })}
        onDelete={(id) => deleteEntryMut.mutate(id)}
        onOpenContact={openContact}
      />

      {/* 6. Weiterentwicklung NORA */}
      <TextSection
        section={SECTIONS[3]}
        items={data.entries.weiterentwicklung}
        onAdd={() => setEntryDialog({ section: "weiterentwicklung" })}
        onDelete={(id) => deleteEntryMut.mutate(id)}
        onOpenContact={openContact}
      />

      {/* Freie Abschlussnotiz */}
      <section className="crm-card p-6" data-testid="weekly-review-freie-notiz-section">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquareText className="w-4 h-4 text-gray-600" />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-800">Freie Abschlussnotiz</h2>
        </div>
        <Textarea
          value={freieNotiz}
          onChange={(e) => {
            setFreieNotizState(e.target.value);
            setFreieNotizDirty(true);
          }}
          onBlur={() => {
            if (freieNotizDirty) {
              notesMut.mutate(freieNotiz || null);
              setFreieNotizDirty(false);
            }
          }}
          rows={4}
          placeholder="Was ist dir zum Abschluss der Woche wichtig? Was möchtest du festhalten?"
          data-testid="weekly-review-freie-notiz"
        />
        <p className="text-xs text-gray-400 mt-1">
          Wird beim Klick außerhalb des Feldes automatisch gespeichert. Beim Abschließen zusätzlich in das Protokoll übernommen.
        </p>
      </section>

      {/* Verlauf */}
      <HistorySection logs={data.logs || []} />

      {/* Dialoge */}
      <EntryDialog
        state={entryDialog}
        onClose={() => setEntryDialog(null)}
        onSubmit={(payload) => {
          createEntryMut.mutate(payload, { onSuccess: () => setEntryDialog(null) });
        }}
        contactOptions={contactOptions}
        challengeOptions={challengeOptions}
      />
      <ObservationDialog
        open={observationDialog}
        onClose={() => setObservationDialog(false)}
        onSubmit={(payload) => {
          createObsMut.mutate(payload, { onSuccess: () => setObservationDialog(false) });
        }}
        contactOptions={contactOptions}
        challengeOptions={challengeOptions}
      />
      <ActivityDialog
        open={activityDialog}
        onClose={() => setActivityDialog(false)}
        onSubmit={(payload) => {
          createActivityMut.mutate(payload, { onSuccess: () => setActivityDialog(false) });
        }}
        contactOptions={contactOptions}
        challengeOptions={challengeOptions}
      />
    </div>
  );
}

// ---------- Wochen-Auswahl ----------

function WeekPicker({ current, options, onChange }) {
  const value = `${current.iso_year}-${current.iso_week}`;
  return (
    <Select
      value={value}
      onValueChange={(v) => {
        const [y, w] = v.split("-").map(Number);
        onChange(y, w);
      }}
    >
      <SelectTrigger className="w-[280px]" data-testid="weekly-review-week-picker">
        <SelectValue placeholder="Woche wählen" />
      </SelectTrigger>
      <SelectContent>
        {(options || []).map((opt) => (
          <SelectItem
            key={`${opt.iso_year}-${opt.iso_week}`}
            value={`${opt.iso_year}-${opt.iso_week}`}
            data-testid={`weekly-review-week-option-${opt.iso_year}-${opt.iso_week}`}
          >
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ---------- Abschnitts-Header ----------

function SectionHeader({ section, count, action }) {
  const Icon = section.icon;
  return (
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-start gap-3">
        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold border ${section.tint} bg-white border-current shrink-0`}>
          {section.number}
        </span>
        <div>
          <h2 className="text-base md:text-lg font-display font-semibold text-gray-900 flex items-center gap-2">
            <Icon className={`w-4 h-4 ${section.tint}`} />
            {section.title}
          </h2>
          {typeof count === "number" ? (
            <p className="text-xs text-gray-500 mt-0.5">{count} {count === 1 ? "Eintrag" : "Einträge"}</p>
          ) : null}
        </div>
      </div>
      {action}
    </div>
  );
}

// ---------- Sektion für Freitext-Einträge (Erfolge, Wartende, Aussichtsreiche, Weiterentwicklung) ----------

function TextSection({ section, items, onAdd, onDelete, onOpenContact }) {
  return (
    <section
      className="crm-card p-6"
      data-testid={`weekly-review-section-${section.key}`}
    >
      <SectionHeader
        section={section}
        count={items.length}
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={onAdd}
            data-testid={`weekly-review-add-${section.key}`}
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Eintrag hinzufügen
          </Button>
        }
      />
      {items.length === 0 ? (
        <div className="text-sm text-gray-500 italic px-2 py-6 text-center border border-dashed border-gray-200 rounded-md">
          Noch nichts festgehalten.
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((it) => (
            <li
              key={it.id}
              className="flex items-start gap-3 bg-white border border-gray-200 rounded-lg p-3"
              data-testid={`weekly-review-entry-${it.id}`}
            >
              <span className={`w-2 h-2 rounded-full mt-2 shrink-0 ${section.dot}`} />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-800 whitespace-pre-line leading-relaxed">{it.text}</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {it.contact_name ? (
                    <ContactChip name={it.contact_name} ampel={it.contact_ampel_status} onClick={() => onOpenContact(it.contact_id)} />
                  ) : null}
                  {it.challenge_title ? (
                    <span className="inline-flex items-center text-[11px] px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200 text-gray-700">
                      {it.challenge_title}
                    </span>
                  ) : null}
                </div>
              </div>
              <button
                onClick={() => onDelete(it.id)}
                className="p-1 text-gray-400 hover:text-rose-600"
                data-testid={`weekly-review-entry-delete-${it.id}`}
                aria-label="Eintrag löschen"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------- Beobachtungen (Sektion 3) ----------

function ObservationsSection({ observations, onAdd, onDelete, onEvaluate, onOpenContact }) {
  const section = {
    key: "muster",
    number: 3,
    title: "Beobachtungen und Muster",
    icon: Eye,
    tint: "text-slate-700",
    dot: "bg-slate-500",
  };
  return (
    <section className="crm-card p-6" data-testid="weekly-review-section-muster">
      <SectionHeader
        section={section}
        count={observations.length}
        action={
          <Button variant="outline" size="sm" onClick={onAdd} data-testid="weekly-review-add-observation">
            <Plus className="w-3.5 h-3.5 mr-1" /> Beobachtung hinzufügen
          </Button>
        }
      />
      <p className="text-xs text-gray-500 mb-3 italic">
        NORA zeigt Muster – bewertet aber niemals Menschen. Formuliere sachlich und mit Bezug zur Situation.
      </p>
      {observations.length === 0 ? (
        <div className="text-sm text-gray-500 italic px-2 py-6 text-center border border-dashed border-gray-200 rounded-md">
          Noch keine Beobachtungen für diese Woche.
        </div>
      ) : (
        <ul className="space-y-3">
          {observations.map((o) => (
            <li
              key={o.id}
              className="bg-white border border-gray-200 rounded-lg p-3"
              data-testid={`weekly-review-observation-${o.id}`}
            >
              <div className="flex items-start gap-3">
                <Eye className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-800 whitespace-pre-line leading-relaxed">{o.text}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {o.contact_name ? (
                      <ContactChip name={o.contact_name} ampel={o.contact_ampel_status} onClick={() => onOpenContact(o.contact_id)} />
                    ) : null}
                    {o.challenge_title ? (
                      <span className="inline-flex items-center text-[11px] px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200 text-gray-700">
                        {o.challenge_title}
                      </span>
                    ) : null}
                    <span className="text-[11px] text-gray-400">Notiert am {formatDeDate(o.occurred_at)}</span>
                  </div>
                </div>
                <button
                  onClick={() => onDelete(o.id)}
                  className="p-1 text-gray-400 hover:text-rose-600"
                  aria-label="Beobachtung löschen"
                  data-testid={`weekly-review-observation-delete-${o.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap items-center gap-2">
                <span className="text-[11px] uppercase tracking-wider text-gray-500">Bewertung Muster</span>
                <EvalGroupPicker
                  options={EVAL_GROUP_B}
                  selected={o.evaluation_value}
                  onPick={(value, note) => onEvaluate(o.id, value, note)}
                  testidPrefix={`weekly-review-observation-eval-${o.id}`}
                />
                {o.evaluation_value ? (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-700">
                    {EVAL_LABEL[o.evaluation_value] || o.evaluation_value}
                  </span>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------- NORA-Aktivitäten (Sektion 4) ----------

function ActivitiesSection({ activities, summary, onAdd, onDelete, onEvaluate, onOpenContact }) {
  const section = {
    key: "activities",
    number: 4,
    title: "Was NORA diese Woche bearbeitet hat",
    icon: Star,
    tint: "text-indigo-700",
    dot: "bg-indigo-500",
  };
  const summarySentence = summaryText(summary);
  return (
    <section
      className="crm-card p-6 bg-indigo-50/30 border-indigo-100"
      data-testid="weekly-review-section-activities"
    >
      <SectionHeader
        section={section}
        count={summary.activities_total}
        action={
          <Button variant="outline" size="sm" onClick={onAdd} data-testid="weekly-review-add-activity">
            <Plus className="w-3.5 h-3.5 mr-1" /> Aktivität anlegen
          </Button>
        }
      />
      <p className="text-sm text-indigo-900/80 leading-relaxed mb-4" data-testid="weekly-review-activities-summary">
        {summarySentence}
      </p>
      {activities.length === 0 ? (
        <div className="text-sm text-gray-500 italic px-2 py-6 text-center border border-dashed border-gray-200 rounded-md bg-white">
          NORA hat in dieser Woche noch nichts eigenständig erledigt.
        </div>
      ) : (
        <ol className="space-y-3">
          {activities.map((a) => (
            <ActivityItem
              key={a.id}
              activity={a}
              onDelete={() => onDelete(a.id)}
              onEvaluate={(group, value, note) => onEvaluate(a.id, group, value, note)}
              onOpenContact={onOpenContact}
            />
          ))}
        </ol>
      )}
    </section>
  );
}

function summaryText(summary) {
  const total = summary.activities_total || 0;
  if (total === 0) return "Diese Woche wurden noch keine internen Vorgänge dokumentiert.";
  const status = summary.activities_by_status || {};
  const erledigt = status.automatisch_erledigt || 0;
  const pruefung = status.zur_pruefung || 0;
  const korrigiert = status.korrigiert || 0;

  // Two forms per statement:
  // - `first`: verb-first (natural after "Davon …")
  // - `rest`:  subject-first (natural after a comma or "und")
  const parts = [];
  if (erledigt > 0) {
    const verb = erledigt === 1 ? "ist" : "sind";
    parts.push({
      first: `${verb} ${erledigt} erledigt`,
      rest: `${erledigt} ${verb} erledigt`,
    });
  }
  if (pruefung > 0) {
    const verb = pruefung === 1 ? "wartet" : "warten";
    parts.push({
      first: `${verb} ${pruefung} auf deine Prüfung`,
      rest: `${pruefung} ${verb} auf deine Prüfung`,
    });
  }
  if (korrigiert > 0) {
    const verb = korrigiert === 1 ? "wurde" : "wurden";
    parts.push({
      first: `${verb} ${korrigiert} nach deiner Rückmeldung angepasst`,
      rest: `${korrigiert} ${verb} nach deiner Rückmeldung angepasst`,
    });
  }

  const strs = parts.map((p, i) => (i === 0 ? p.first : p.rest));

  let joined = "";
  if (strs.length === 1) {
    joined = strs[0];
  } else if (strs.length === 2) {
    joined = `${strs[0]} und ${strs[1]}`;
  } else if (strs.length >= 3) {
    joined = `${strs.slice(0, -1).join(", ")} und ${strs[strs.length - 1]}`;
  }

  const noun = total === 1 ? "internen Vorgang" : "interne Vorgänge";
  const first = `Diese Woche habe ich ${total} ${noun} bearbeitet.`;
  if (!joined) return first;
  return `${first} Davon ${joined}.`;
}

function ActivityItem({ activity, onDelete, onOpenContact, onEvaluate }) {
  const status = STATUS_MAP[activity.status];
  return (
    <li
      className="bg-white rounded-lg border border-gray-200 p-3"
      data-testid={`weekly-review-activity-${activity.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${status?.cls || "bg-gray-100 text-gray-600 border-gray-200"} border`}>
              {status?.label || activity.status}
            </span>
            <span className="text-[11px] text-gray-500">{CATEGORY_LABEL[activity.category] || activity.category}</span>
            <span className="text-[11px] text-gray-400">· {formatDeDate(activity.occurred_at)}</span>
          </div>
          <p className="text-sm font-medium text-gray-900 leading-snug">{activity.action_text}</p>
          {activity.reason_text ? (
            <p className="text-xs text-gray-600 mt-1"><span className="uppercase tracking-wider text-[10px] text-gray-500">Warum:</span> {activity.reason_text}</p>
          ) : null}
          {activity.information_used ? (
            <p className="text-xs text-gray-600 mt-0.5"><span className="uppercase tracking-wider text-[10px] text-gray-500">Basis:</span> {activity.information_used}</p>
          ) : null}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {activity.contact_name ? (
              <ContactChip name={activity.contact_name} ampel={activity.contact_ampel_status} onClick={() => onOpenContact(activity.contact_id)} />
            ) : null}
            {activity.challenge_title ? (
              <span className="inline-flex items-center text-[11px] px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200 text-gray-700">
                {activity.challenge_title}
              </span>
            ) : null}
          </div>
        </div>
        <button
          onClick={onDelete}
          className="p-1 text-gray-400 hover:text-rose-600"
          aria-label="Aktivität löschen"
          data-testid={`weekly-review-activity-delete-${activity.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Bewertung: Lernbaukasten */}
      <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
        <EvalRow label="Handlung" options={EVAL_GROUP_A}
                 selected={activity.evaluation_group === "A" ? activity.evaluation_value : null}
                 onPick={(v, n) => onEvaluate("A", v, n)}
                 testidPrefix={`weekly-review-activity-eval-A-${activity.id}`} />
        <EvalRow label="Muster" options={EVAL_GROUP_B}
                 selected={activity.evaluation_group === "B" ? activity.evaluation_value : null}
                 onPick={(v, n) => onEvaluate("B", v, n)}
                 testidPrefix={`weekly-review-activity-eval-B-${activity.id}`} />
        <EvalRow label="Vorbereitung" options={EVAL_GROUP_C}
                 selected={activity.evaluation_group === "C" ? activity.evaluation_value : null}
                 onPick={(v, n) => onEvaluate("C", v, n)}
                 testidPrefix={`weekly-review-activity-eval-C-${activity.id}`} />
        <EvalRow label="Anderes" options={[{ value: "anderes", label: "Freitext / Sprachnotiz" }]}
                 selected={activity.evaluation_group === "andere" ? activity.evaluation_value : null}
                 onPick={(v, n) => onEvaluate("andere", v, n)}
                 requireNote
                 testidPrefix={`weekly-review-activity-eval-andere-${activity.id}`} />
        {activity.evaluation_note ? (
          <p className="text-xs text-gray-600 pl-1">
            <span className="uppercase text-[10px] tracking-wider text-gray-500">Notiz zur Bewertung:</span> {activity.evaluation_note}
          </p>
        ) : null}
      </div>
    </li>
  );
}

function EvalRow({ label, options, selected, onPick, requireNote, testidPrefix }) {
  return (
    <div className="flex items-start gap-2 flex-wrap">
      <span className="text-[11px] font-medium uppercase tracking-wider text-gray-500 min-w-[92px] pt-1">{label}</span>
      <EvalGroupPicker options={options} selected={selected} onPick={onPick} requireNote={requireNote} testidPrefix={testidPrefix} />
    </div>
  );
}

function EvalGroupPicker({ options, selected, onPick, requireNote, testidPrefix }) {
  const [noteState, setNoteState] = useState({ open: false, value: null });
  return (
    <div className="flex flex-wrap gap-1">
      {options.map((opt) => {
        const isSel = selected === opt.value;
        const needsNote = requireNote || ["anders_machen", "hier_vorher_fragen", "nicht_mehr_machen", "anderes"].includes(opt.value);
        return (
          <div key={opt.value} className="relative">
            <button
              type="button"
              onClick={() => {
                if (needsNote) {
                  setNoteState({ open: true, value: opt.value });
                } else {
                  onPick(opt.value, null);
                }
              }}
              className={`text-[11px] px-2 py-1 rounded-md border transition-colors ${
                isSel
                  ? "bg-gray-900 text-white border-gray-900"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
              }`}
              data-testid={`${testidPrefix}-${opt.value}`}
              title={opt.hint || ""}
            >
              {opt.label}
            </button>
          </div>
        );
      })}
      <NotePromptDialog
        state={noteState}
        onClose={() => setNoteState({ open: false, value: null })}
        onSubmit={(text) => {
          onPick(noteState.value, text || null);
          setNoteState({ open: false, value: null });
        }}
      />
    </div>
  );
}

function NotePromptDialog({ state, onClose, onSubmit }) {
  const [text, setText] = useState("");
  const open = !!state?.open;
  const label = state?.value ? EVAL_LABEL[state.value] || state.value : "";
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setText(""); onClose(); } }}>
      <DialogContent data-testid="weekly-review-eval-note-dialog">
        <DialogHeader>
          <DialogTitle>Kurze Notiz zur Bewertung</DialogTitle>
          <DialogDescription>
            {`Für „${label}“ ist eine kurze Notiz hilfreich, aber nicht zwingend.`}
          </DialogDescription>
        </DialogHeader>
        <Textarea
          rows={3}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Was war der Grund? (optional)"
          data-testid="weekly-review-eval-note-text"
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => { setText(""); onClose(); }} data-testid="weekly-review-eval-note-cancel">Abbrechen</Button>
          <Button
            className="bg-black text-white hover:bg-gray-800"
            onClick={() => { onSubmit(text); setText(""); }}
            data-testid="weekly-review-eval-note-save"
          >
            Bewertung speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Dialoge ----------

function EntryDialog({ state, onClose, onSubmit, contactOptions, challengeOptions }) {
  const [text, setText] = useState("");
  const [contactId, setContactId] = useState("");
  const [challengeId, setChallengeId] = useState("");

  const open = !!state;
  const section = SECTIONS.find((s) => s.key === state?.section);

  const handleSubmit = () => {
    if (!text.trim()) {
      toast.error("Bitte gib einen Text ein.");
      return;
    }
    onSubmit({
      section: state.section,
      text: text.trim(),
      contact_id: contactId || null,
      challenge_id: challengeId || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => {
      if (v) {
        setText("");
        setContactId("");
        setChallengeId("");
      } else {
        onClose();
      }
    }}>
      <DialogContent data-testid="weekly-review-entry-dialog">
        <DialogHeader>
          <DialogTitle>{section ? section.title : "Eintrag hinzufügen"}</DialogTitle>
          <DialogDescription>Ein Eintrag für den Wochenrückblick. Optional mit Bezug.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Text</Label>
            <Textarea
              rows={4}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={section?.placeholder}
              data-testid="weekly-review-entry-text"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Person (optional)</Label>
            <RelationSelect value={contactId} onChange={setContactId} options={contactOptions} placeholder="– keine Person –" testid="weekly-review-entry-contact" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Herausforderung (optional)</Label>
            <RelationSelect value={challengeId} onChange={setChallengeId} options={challengeOptions} placeholder="– keine Herausforderung –" testid="weekly-review-entry-challenge" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="weekly-review-entry-cancel">Abbrechen</Button>
          <Button className="bg-black text-white hover:bg-gray-800" onClick={handleSubmit} data-testid="weekly-review-entry-save">
            Eintrag speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ObservationDialog({ open, onClose, onSubmit, contactOptions, challengeOptions }) {
  const [text, setText] = useState("");
  const [contactId, setContactId] = useState("");
  const [challengeId, setChallengeId] = useState("");

  const handleSubmit = () => {
    if (!text.trim()) {
      toast.error("Bitte gib eine Beobachtung ein.");
      return;
    }
    onSubmit({
      text: text.trim(),
      contact_id: contactId || null,
      challenge_id: challengeId || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => {
      if (v) {
        setText("");
        setContactId("");
        setChallengeId("");
      } else {
        onClose();
      }
    }}>
      <DialogContent data-testid="weekly-review-observation-dialog">
        <DialogHeader>
          <DialogTitle>Beobachtung ergänzen</DialogTitle>
          <DialogDescription>
            Sachlich formulieren – NORA beschreibt Muster, keine Menschen.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Beobachtung</Label>
            <Textarea
              rows={4}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder='Zum Beispiel: „Bei dieser Herausforderung wurden zwei Rückmeldungen später als vereinbart gegeben."'
              data-testid="weekly-review-observation-text"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Person (optional)</Label>
            <RelationSelect value={contactId} onChange={setContactId} options={contactOptions} placeholder="– keine Person –" testid="weekly-review-observation-contact" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Herausforderung (optional)</Label>
            <RelationSelect value={challengeId} onChange={setChallengeId} options={challengeOptions} placeholder="– keine Herausforderung –" testid="weekly-review-observation-challenge" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="weekly-review-observation-cancel">Abbrechen</Button>
          <Button className="bg-black text-white hover:bg-gray-800" onClick={handleSubmit} data-testid="weekly-review-observation-save">
            Beobachtung speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ActivityDialog({ open, onClose, onSubmit, contactOptions, challengeOptions }) {
  const [actionText, setActionText] = useState("");
  const [reasonText, setReasonText] = useState("");
  const [infoText, setInfoText] = useState("");
  const [category, setCategory] = useState("sonstiges");
  const [status, setStatus] = useState("automatisch_erledigt");
  const [contactId, setContactId] = useState("");
  const [challengeId, setChallengeId] = useState("");

  const reset = () => {
    setActionText("");
    setReasonText("");
    setInfoText("");
    setCategory("sonstiges");
    setStatus("automatisch_erledigt");
    setContactId("");
    setChallengeId("");
  };

  const handleSubmit = () => {
    if (!actionText.trim()) {
      toast.error("Bitte beschreibe, was NORA getan hat.");
      return;
    }
    onSubmit({
      action_text: actionText.trim(),
      reason_text: reasonText.trim() || null,
      information_used: infoText.trim() || null,
      category,
      status,
      contact_id: contactId || null,
      challenge_id: challengeId || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => {
      if (v) {
        reset();
      } else {
        onClose();
      }
    }}>
      <DialogContent className="max-w-lg" data-testid="weekly-review-activity-dialog">
        <DialogHeader>
          <DialogTitle>NORA-Aktivität anlegen</DialogTitle>
          <DialogDescription>Dokumentiert nachvollziehbar, was NORA eigenständig erledigt hat.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Was hat NORA getan?</Label>
            <Textarea rows={2} value={actionText} onChange={(e) => setActionText(e.target.value)} data-testid="weekly-review-activity-action" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Warum hat NORA es getan? (optional)</Label>
            <Textarea rows={2} value={reasonText} onChange={(e) => setReasonText(e.target.value)} data-testid="weekly-review-activity-reason" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Welche Informationen wurden genutzt? (optional)</Label>
            <Input value={infoText} onChange={(e) => setInfoText(e.target.value)} data-testid="weekly-review-activity-info" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Kategorie</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="weekly-review-activity-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACTIVITY_CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value} data-testid={`weekly-review-activity-category-${c.value}`}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="weekly-review-activity-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value} data-testid={`weekly-review-activity-status-${s.value}`}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Person (optional)</Label>
            <RelationSelect value={contactId} onChange={setContactId} options={contactOptions} placeholder="– keine Person –" testid="weekly-review-activity-contact" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Herausforderung (optional)</Label>
            <RelationSelect value={challengeId} onChange={setChallengeId} options={challengeOptions} placeholder="– keine Herausforderung –" testid="weekly-review-activity-challenge" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="weekly-review-activity-cancel">Abbrechen</Button>
          <Button className="bg-black text-white hover:bg-gray-800" onClick={handleSubmit} data-testid="weekly-review-activity-save">
            Aktivität speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RelationSelect({ value, onChange, options, placeholder, testid }) {
  const NONE = "__none__";
  return (
    <Select value={value || NONE} onValueChange={(v) => onChange(v === NONE ? "" : v)}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="max-h-72">
        <SelectItem value={NONE}>{placeholder}</SelectItem>
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ---------- Verlauf ----------

function HistorySection({ logs }) {
  const [openId, setOpenId] = useState(null);
  if (!logs.length) return null;
  return (
    <section className="crm-card p-6" data-testid="weekly-review-history">
      <div className="flex items-center gap-2 mb-4">
        <History className="w-4 h-4 text-gray-600" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-800">Verlauf der Wochenprotokolle</h2>
        <span className="text-xs text-gray-500">({logs.length})</span>
      </div>
      <ol className="space-y-2">
        {logs.map((log, idx) => {
          const isOpen = openId === log.id;
          let snapshot = null;
          try {
            snapshot = JSON.parse(log.snapshot_json || "{}");
          } catch (e) { snapshot = null; }
          return (
            <li key={log.id} className="bg-white border border-gray-200 rounded-lg" data-testid={`weekly-review-history-entry-${idx}`}>
              <button
                type="button"
                onClick={() => setOpenId(isOpen ? null : log.id)}
                className="w-full flex items-start justify-between gap-3 px-3 py-2 text-left hover:bg-gray-50"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-gray-900 text-sm">{formatDeDate(log.closed_at)}</span>
                  {idx === 0 ? (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">Neuester</span>
                  ) : null}
                  <span className="text-xs text-gray-500">
                    {log.count_erfolge} Erfolge · {log.count_wartende} wartend · {log.count_beobachtungen} Muster · {log.count_activities} NORA
                  </span>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 mt-1 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && snapshot ? (
                <div className="border-t border-gray-100 p-3 text-sm space-y-3">
                  <ProtocolReadonly snapshot={snapshot} />
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function ProtocolReadonly({ snapshot }) {
  const s = snapshot || {};
  const sections = s.sections || {};
  const observations = s.observations || [];
  const activities = s.activities || [];
  const counts = s.counts || {};

  const renderList = (title, arr) => (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">{title} ({arr.length})</div>
      {arr.length === 0 ? (
        <p className="text-xs text-gray-400 italic mt-1">—</p>
      ) : (
        <ul className="mt-1 space-y-1">
          {arr.map((it, i) => (
            <li key={i} className="text-sm text-gray-800">• {it.text || it.action_text}</li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="text-xs text-gray-500">{s.week_label}</div>
      {renderList("Erfolge und Rückblick", sections.erfolge || [])}
      {renderList("Offene Punkte", sections.wartende || [])}
      {renderList("Beobachtungen und Muster", observations)}
      {renderList("Schwerpunkte für die nächste Woche", sections.aussichtsreiche || [])}
      {renderList("NORA-Aktivitäten", activities)}
      {renderList("Weiterentwicklung NORA", sections.weiterentwicklung || [])}
      {s.freie_notiz ? (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Freie Abschlussnotiz</div>
          <p className="text-sm text-gray-800 whitespace-pre-line mt-1">{s.freie_notiz}</p>
        </div>
      ) : null}
      <p className="text-[11px] text-gray-400">
        Zählung: {counts.erfolge || 0} Erfolge · {counts.wartende || 0} wartend · {counts.beobachtungen || 0} Muster · {counts.activities || 0} NORA · {counts.aussichtsreiche || 0} Aussicht · {counts.weiterentwicklung || 0} Weiterentwicklung
      </p>
    </div>
  );
}
